import React, { useCallback } from 'react';
import { AnyChart } from '@d3fc/d3fc-chart';
import { useXAxisContext } from './XAxisContext';
import { renderTimeBreaks } from './timebreaks';
import { createChartSeries } from './timeline';
import { renderXAxis } from './xAxis';
import { renderYAxis } from './yAxis';
import './TimeSeriesChart.scss';
import classNames from 'classnames';
import { useHorizontalZoomContext } from 'utils/components/Zoom/ZoomContext';
import { Dimensions } from 'utils/types/Dimensions';
import { CartesianChart } from 'utils/components/CartesianChart/CartesianChart';
import { BaseType, Selection } from 'd3-selection';
import { AnySelection } from '@d3fc/d3fc-series';

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface TimeSeriesProps {
    id: string;
    height: number;
    className?: string;
    configureTickIndexesToShow?: (
        selection: AnySelection<SVGElement>,
        tickIndexesToShow: number[]
    ) => void;
    data: any[];
    series: any;
    displayXAxis: boolean;
    yScale: any;
    yTicks: number;
    yTickFormat?: (d: number) => string;
    // Use this to switch off zoom for example if you wish to support drag as in a tile
    suppressZoom?: boolean;
    onChartClick?: (time: number | undefined) => void;
    decorate?: (sel: Selection<BaseType, unknown, null, undefined>) => void;
    enrichChart?: (node: HTMLDivElement, chart: AnyChart) => void;
}

export default function TimeSeriesChart(props: TimeSeriesProps) {
    const {
        id,
        className,
        configureTickIndexesToShow,
        height,
        series,
        data,
        displayXAxis,
        yScale,
        yTicks,
        yTickFormat,
        suppressZoom,
        onChartClick: onPropsChartClick,
        enrichChart: enrichChartProps,
        decorate,
    } = props;

    const {
        timeSeriesChartWidth,
        plotAreaWidth,
        isDomainValid,
        breaks,
        breaksType,
        rangeLabels,
        timeLineLabels,
        timePointerRef,
        setPlotAreaWidth,
        showTimeLineDate,
    } = useXAxisContext();

    const {
        startX: startTime,
        endX: endTime,
        zoomedXScale: xScale,
    } = useHorizontalZoomContext();

    const { renderTimeLine, renderTimeRect, applyPointer } =
        timePointerRef.current;

    const chartSeries = createChartSeries(series, timeLineLabels);

    const onPlotAreaDimensionsChanged = (nextDimensions: Dimensions) => {
        setPlotAreaWidth(nextDimensions.width);
    };

    const enrichChart = useCallback(
        (node: HTMLDivElement, chart: AnyChart) => {
            if (enrichChartProps) {
                enrichChartProps(node, chart);
            }
            renderXAxis(
                chart,
                id,
                plotAreaWidth,
                displayXAxis,
                isDomainValid,
                startTime,
                endTime,
                xScale,
                breaks,
                breaksType ?? 'default',
                rangeLabels,
                timeLineLabels,
                renderTimeRect,
                showTimeLineDate,
                configureTickIndexesToShow
            );
            renderYAxis(chart, data, yScale, yTicks, yTickFormat);

            applyPointer(node, isDomainValid, xScale);
            renderTimeLine(
                node,
                id,
                isDomainValid,
                breaks,
                breaksType ?? 'default'
            );
            renderTimeBreaks(node, isDomainValid, breaks, xScale);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            id,
            plotAreaWidth,
            displayXAxis,
            isDomainValid,
            startTime,
            endTime,
            xScale,
            breaks,
            breaksType,
            rangeLabels,
            timeLineLabels,
            data,
            yScale,
            yTicks,
            yTickFormat,
            enrichChartProps,
            configureTickIndexesToShow,
        ]
    );

    const cssClasses = classNames(' TimeSeriesChart', className);

    const onChartClick = () => {
        const current = timePointerRef.current.getLatest()?.time;
        if (onPropsChartClick) {
            onPropsChartClick(current);
        }
    };

    return (
        <CartesianChart
            chartSeries={chartSeries}
            className={cssClasses}
            configureTickIndexesToShow={configureTickIndexesToShow}
            data={data}
            decorate={decorate}
            enrichChart={enrichChart}
            id={id}
            height={height}
            width={timeSeriesChartWidth}
            onPlotAreaDimensionsChanged={onPlotAreaDimensionsChanged}
            yScale={yScale}
            onClickChart={onChartClick}
            suppressZoom={suppressZoom}
            yOrient={'right'}
            xOrient={displayXAxis ? 'bottom' : undefined}
        />
    );
}
